import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { identity } from 'lodash-es';
import { useDevice } from '../../providers/Device';
import { getTestIds } from '../../utils/TestIds';
import { Autocomplete } from '../Autocomplete';
import { Grid } from '../Grid';
import { GridItem } from '../GridItem';
import { MenuItem } from '../MenuItem';
import { Select } from '../Select';
import { TextField } from '../TextField';
import { Typography } from '../Typography';
import { isAddressFieldsValueValid } from './AddressFields.utils';
import { useAddressFieldsValidation } from './hooks/useAddressFieldsValidation';
export const GetAddressFieldsTID = (parentTestId)=>getTestIds(parentTestId);
export const AddressFields = (props)=>{
    const { value, onChange, errorTexts, showAllErrors = false, allowEmpty = false, allowPoBox = false, labels, placeholders, stateOptions, suggestions, isLoadingSuggestions, onStreetInputChange, onSelectSuggestion, disabled, testId } = props;
    const { isMobileDevice } = useDevice();
    const { AddressFieldsTestIds } = useMemo(()=>GetAddressFieldsTID(testId), [
        testId
    ]);
    const { Fields, Cells } = AddressFieldsTestIds;
    const { visibleError, markTouched } = useAddressFieldsValidation({
        value,
        errorTexts,
        showAllErrors,
        allowEmpty,
        allowPoBox
    });
    function changeValue(nextValue) {
        onChange(nextValue, {
            isValid: isAddressFieldsValueValid(nextValue, {
                allowEmpty,
                allowPoBox
            })
        });
    }
    function biFor(field) {
        return props.noBi ? {
            noBi: props.noBi
        } : {
            bi: props.bi[field]
        };
    }
    function fieldProps(field, fieldTestId) {
        return _object_spread({
            testId: fieldTestId,
            label: labels[field],
            placeholder: placeholders === null || placeholders === void 0 ? void 0 : placeholders[field],
            value: value[field],
            onChange: (event)=>changeValue(_object_spread_props(_object_spread({}, value), {
                    [field]: event.target.value
                })),
            onBlur: ()=>markTouched(field),
            error: !!visibleError(field),
            helperText: visibleError(field),
            collapseHelperText: false,
            disabled
        }, biFor(field));
    }
    function handleStreetInputChange(_event, query, reason) {
        if (reason === 'input') {
            changeValue(_object_spread_props(_object_spread({}, value), {
                address1: query
            }));
            onStreetInputChange(query);
            if (isMobileDevice) {
                markTouched('address1');
            }
        }
    }
    function handleSuggestionPicked(_event, picked) {
        if (typeof picked !== 'string') {
            onSelectSuggestion(picked.id);
        }
    }
    function renderSuggestion(suggestion) {
        return /*#__PURE__*/ _jsxs(Typography, {
            variant: "paragraph3",
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    as: "span",
                    variant: "paragraph2",
                    weight: "bold",
                    children: suggestion.primaryText
                }),
                ' ',
                suggestion.secondaryText
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Grid, {
        rowGap: "small",
        testId: AddressFieldsTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(GridItem, {
                span: 12,
                testId: Cells.Address1(),
                children: /*#__PURE__*/ _jsx(Autocomplete, _object_spread({
                    testId: Fields.Address1(),
                    freeSolo: true,
                    disableClearable: true,
                    disableEndAdornment: isMobileDevice,
                    collapseHelperText: false,
                    options: suggestions,
                    filterOptions: identity,
                    loading: isLoadingSuggestions,
                    value: value.address1,
                    inputValue: value.address1,
                    getOptionLabel: (option)=>typeof option === 'string' ? option : option.primaryText,
                    renderOption: renderSuggestion,
                    onInputChange: handleStreetInputChange,
                    onChange: handleSuggestionPicked,
                    onBlur: ()=>markTouched('address1'),
                    error: !!visibleError('address1'),
                    helperText: visibleError('address1'),
                    disabled: disabled,
                    textFieldProps: {
                        label: labels.address1,
                        placeholder: placeholders === null || placeholders === void 0 ? void 0 : placeholders.address1
                    }
                }, biFor('address1')))
            }),
            /*#__PURE__*/ _jsx(GridItem, {
                span: 12,
                testId: Cells.Address2(),
                children: /*#__PURE__*/ _jsx(TextField, _object_spread({}, fieldProps('address2', Fields.Address2())))
            }),
            /*#__PURE__*/ _jsx(GridItem, {
                span: isMobileDevice ? 12 : 4,
                testId: Cells.City(),
                children: /*#__PURE__*/ _jsx(TextField, _object_spread({}, fieldProps('city', Fields.City())))
            }),
            /*#__PURE__*/ _jsx(GridItem, {
                span: isMobileDevice ? 6 : 4,
                testId: Cells.State(),
                children: /*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({}, fieldProps('state', Fields.State())), {
                    children: stateOptions.map((state)=>/*#__PURE__*/ _jsx(MenuItem, {
                            value: state,
                            testId: Fields.StateOption(state),
                            children: state
                        }, state))
                }))
            }),
            /*#__PURE__*/ _jsx(GridItem, {
                span: isMobileDevice ? 6 : 4,
                testId: Cells.PostalCode(),
                children: /*#__PURE__*/ _jsx(TextField, _object_spread({}, fieldProps('postalCode', Fields.PostalCode())))
            })
        ]
    });
};
